import BillingAddressForm from "../components/BillingAddressForm";
import {
  canonicalCountry,
  type BillingAddress,
} from "../../../src/shared/billing-location";
import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  LinearProgress,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Link,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  getBilling,
  getBillingAddress,
  getPlans,
  prepareCheckout,
  subscribe,
  updateCard,
  cancelSubscription,
  type BillingInfo,
  type CheckoutPlan,
  type CancelReason,
} from "@/api/dashboard";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import { extractCardDetails, normalizeCardBrand } from "@/utils/helcim";
import {
  PLANS_CATALOG,
  OVERAGE_RATE_CENTS_PER_RENDER,
  type TierSlug,
} from "../../../src/shared/plans-catalog";
import { planFeatures } from "@/constants/plans";
import { DRAWER_WIDTH, DESKTOP_BREAKPOINT_PX } from "@/layouts/DashboardLayout";

declare global {
  interface Window {
    appendHelcimPayIframe?: (checkoutToken: string) => void;
  }
}

const TIER_ORDER = [
  "free",
  "hobbyist",
  "starter",
  "growth",
  "pro",
  "enterprise",
];

function tierRank(plan: string): number {
  const idx = TIER_ORDER.indexOf(plan);
  return idx >= 0 ? idx : 0;
}

type TileCta =
  | { kind: "current" }
  | { kind: "cancel" }
  | { kind: "contact-sales" }
  | { kind: "upgrade"; planSlug: TierSlug }
  | { kind: "downgrade"; planSlug: TierSlug };

function getTileCta(
  planSlug: TierSlug,
  currentPlan: string,
  currentStatus: string | null,
): TileCta {
  if (planSlug === currentPlan) return { kind: "current" };
  if (planSlug === "free") {
    // User is on a paid plan and clicks Free → "Cancel subscription".
    // If there's nothing to cancel (already expired/canceled), show "Your plan" disabled.
    if (currentStatus === "expired_trial" || currentStatus === "canceled") {
      return { kind: "current" };
    }
    return { kind: "cancel" };
  }
  if (planSlug === "enterprise") return { kind: "contact-sales" };
  const currentRank = tierRank(currentPlan);
  const targetRank = tierRank(planSlug);
  return targetRank > currentRank
    ? { kind: "upgrade", planSlug }
    : { kind: "downgrade", planSlug };
}

function daysUntil(dateStr: string): number {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [plans, setPlans] = useState<CheckoutPlan[]>([]);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "monthly",
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tracks which button triggered checkout: "card" or null (only for Helcim flows)
  const [checkoutTarget, setCheckoutTarget] = useState<string | null>(null);
  const [cardUpdateToken, setCardUpdateToken] = useState<string | null>(null);

  // Tracks which plan is being changed (upgrade/downgrade — no Helcim)
  const [changingPlan, setChangingPlan] = useState<string | null>(null);

  // Upgrade / downgrade confirmation dialogs
  const [pendingUpgrade, setPendingUpgrade] = useState<{
    planSlug: string;
    planName: string;
  } | null>(null);
  const [pendingDowngrade, setPendingDowngrade] = useState<{
    planSlug: string;
    planName: string;
  } | null>(null);

  // Cancel subscription dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState<CancelReason | "">("");
  const [cancelDetail, setCancelDetail] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [resubscribing, setResubscribing] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Any action in progress disables all plan/card buttons
  const busy =
    checkoutTarget !== null ||
    changingPlan !== null ||
    cancelLoading ||
    resubscribing;

  const isWelcome = searchParams.get("welcome") === "1";
  const isSubscribed = searchParams.get("subscribed") === "1";

  // Helcim computes recurring tax from the customer's address, and checkout
  // refuses to create a billing subscription without a complete one. Every
  // subscribe path here bills immediately (no cardless trial), so the address
  // gates all of them — not just the ones that attach a card.
  const [billingAddress, setBillingAddress] = useState<Partial<BillingAddress>>({});
  const [savedAddress, setSavedAddress] = useState<BillingAddress | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [billingData, plansData] = await Promise.all([
        getBilling(),
        getPlans(),
      ]);
      setBilling(billingData);
      setPlans(plansData);
      setBillingCycle(
        (billingData.billingCycle as "monthly" | "annual") || "monthly",
      );
      return billingData;
    } catch {
      setError("Failed to load billing info");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Prefill from what checkout already holds. Without this an existing
  // customer sees an empty form and reads it as their details having been
  // lost, and has to retype an address that never changed before the plan
  // buttons will work.
  useEffect(() => {
    let cancelled = false;
    getBillingAddress()
      .then((stored) => {
        if (cancelled || !stored) return;
        // An address saved before the three-letter migration comes back as
        // "US"/"CA", which matches no option in the country picker — the field
        // would render blank and read as lost data.
        const prefill = { ...stored, country: canonicalCountry(stored.country) };
        setBillingAddress(prefill);
        setSavedAddress(prefill);
      })
      .catch(() => {
        /* prefill is a convenience — never block the page on it */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load HelcimPay.js script for direct card update flow
  useEffect(() => {
    // PDF-12: HelcimPay.js is not preloaded from index.html. It is loaded
    // here from BillingPage on mount instead of globally, so non-billing
    // routes never request secure.helcim.app.
    const exists = document.querySelector(
      'script[src*="helcim-pay/services/start.js"]',
    );
    if (!exists) {
      const script = document.createElement("script");
      script.src = "https://secure.helcim.app/helcim-pay/services/start.js";
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleUpdateCardSuccess = useCallback(
    async (
      cardToken: string,
      cardLast4?: string | null,
      cardBrand?: string | null,
    ) => {
      setError(null);
      let updateErr: unknown = null;
      try {
        await updateCard(cardToken, cardLast4, cardBrand);
      } catch (err) {
        // The card-save round-trip includes payment-provider calls and can take
        // a few seconds; the client may abort before the server responds even
        // though the card was saved. Always re-fetch below to reflect the true
        // state, and only surface an error if the card genuinely isn't on file.
        updateErr = err;
      }
      const latest = await fetchData();
      if (updateErr && !latest?.hasPaymentMethod) {
        setError(
          updateErr instanceof Error
            ? updateErr.message
            : "Failed to update payment method",
        );
      }
      setCheckoutTarget(null);
    },
    [fetchData],
  );

  // Listen for HelcimPay.js postMessage (card update flow only)
  useEffect(() => {
    if (!cardUpdateToken) return;
    const handler = (event: MessageEvent) => {
      if (event.data.eventName !== `helcim-pay-js-${cardUpdateToken}`) return;

      if (event.data.eventStatus === "SUCCESS") {
        // Remove the Helcim iframe immediately — it may linger invisibly and
        // block pointer events on the page beneath it.
        document.getElementById("helcimPayIframe")?.remove();

        const details = extractCardDetails(event.data.eventMessage);
        if (!details.cardToken) {
          setError("Payment succeeded but no card token was returned");
          setCheckoutTarget(null);
          setCardUpdateToken(null);
          return;
        }
        setCardUpdateToken(null);
        handleUpdateCardSuccess(
          details.cardToken,
          details.cardLast4,
          details.cardBrand,
        );
      } else if (event.data.eventStatus === "ABORTED") {
        setCheckoutTarget(null);
        setCardUpdateToken(null);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [cardUpdateToken, handleUpdateCardSuccess]);

  // Watch for the Helcim iframe being removed from the DOM (user closed modal, etc.)
  useEffect(() => {
    if (checkoutTarget === null) return;
    const observer = new MutationObserver(() => {
      if (!document.getElementById("helcimPayIframe")) {
        setCheckoutTarget(null);
        setCardUpdateToken(null);
      }
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [checkoutTarget]);

  // Reposition the Helcim iframe to avoid covering the sidebar on desktop;
  // on mobile the sidebar is a temporary drawer, so the iframe should fill the
  // viewport. maxWidth:100vw clamps the iframe element itself so a
  // fixed-width Helcim widget can't push the page wider than the viewport.
  // (overflow on iframes themselves is owned by the iframe document — it
  // can't be controlled from the parent here.)
  const repositionHelcimIframe = () => {
    requestAnimationFrame(() => {
      const iframe = document.getElementById(
        "helcimPayIframe",
      ) as HTMLIFrameElement | null;
      if (!iframe) return;
      const isDesktop = window.matchMedia(
        `(min-width: ${DESKTOP_BREAKPOINT_PX}px)`,
      ).matches;
      iframe.style.left = isDesktop ? `${DRAWER_WIDTH}px` : "0";
      iframe.style.width = isDesktop
        ? `calc(100% - ${DRAWER_WIDTH}px)`
        : "100%";
      iframe.style.maxWidth = "100vw";
    });
  };

  const openHelcimCardUpdate = async () => {
    setError(null);
    setCheckoutTarget("card");
    try {
      const config = await prepareCheckout(savedAddress ?? undefined);
      setCardUpdateToken(config.checkoutToken);
      if (window.appendHelcimPayIframe) {
        window.appendHelcimPayIframe(config.checkoutToken);
        repositionHelcimIframe();
      } else {
        setError("Payment form not loaded. Please refresh and try again.");
        setCheckoutTarget(null);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Failed to load payment form: ${msg}`);
      setCheckoutTarget(null);
    }
  };

  // Direct plan change (upgrade or downgrade) — no Helcim, just call subscribe
  const handlePlanChange = async (planSlug: string) => {
    setError(null);
    setChangingPlan(planSlug);
    try {
      await subscribe({ planSlug, billingCycle, billingAddress: savedAddress ?? undefined });
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change plan");
    } finally {
      setChangingPlan(null);
    }
  };

  const handleResubscribe = async () => {
    if (!billing) return;
    setError(null);
    setResubscribing(true);
    try {
      await subscribe({
        planSlug: billing.plan,
        billingCycle: billing.billingCycle as "monthly" | "annual",
        activateOn: billing.currentPeriodEnd ?? undefined,
        billingAddress: savedAddress ?? undefined,
      });
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resubscribe");
    } finally {
      setResubscribing(false);
    }
  };

  const handleUpgradeConfirm = () => {
    if (!pendingUpgrade) return;
    const { planSlug } = pendingUpgrade;
    setPendingUpgrade(null);
    handlePlanChange(planSlug);
  };

  const handleDowngradeConfirm = () => {
    if (!pendingDowngrade) return;
    const { planSlug } = pendingDowngrade;
    setPendingDowngrade(null);
    handlePlanChange(planSlug);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelDetail("");
    setCancelError(null);
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setCancelError(null);
    try {
      const result = await cancelSubscription({
        reason: cancelReason || undefined,
        reasonDetail: cancelDetail.trim() || undefined,
      });
      const accessUntil = dayjs.utc(result.accessUntil).format("MMM D, YYYY");
      setCancelDialogOpen(false);
      setCancelReason("");
      setCancelDetail("");
      setSnackbar(`Subscription canceled — access until ${accessUntil}`);
      await fetchData();
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : "Failed to cancel");
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) return <LinearProgress />;

  const currentPlan = billing?.plan ?? "free";
  const isTrialing = billing?.subscriptionStatus === "trialing";
  const trialDaysLeft = billing?.trialEndDate
    ? daysUntil(billing.trialEndDate)
    : 0;
  const trialExpired = isTrialing && trialDaysLeft === 0;
  const isExpiredTrial = billing?.subscriptionStatus === "expired_trial";
  // A plan is always chosen at signup, so the "pick a plan" welcome banner is
  // redundant once a plan/subscription is tied to the account. Only show it as a
  // defensive fallback for an account with no plan tied yet.
  const hasPlanTied =
    currentPlan !== "free" || Boolean(billing?.subscriptionStatus);

  const fmtDate = (iso: string) => dayjs.utc(iso).format("MMM D, YYYY");
  let chargeDateLine: ReactNode = null;
  if (billing && !billing.effectiveCancelAtPeriodEnd) {
    const status = billing.subscriptionStatus;
    if (
      status === "expired_trial" &&
      billing.trialEndDate &&
      !billing.hasPaymentMethod
    ) {
      chargeDateLine = (
        <Typography variant="body1">
          Trial ended <strong>{fmtDate(billing.trialEndDate)}</strong> — add a
          payment method to continue.
        </Typography>
      );
    } else if (status === "trialing" && billing.trialEndDate) {
      chargeDateLine = billing.hasPaymentMethod ? (
        <Typography variant="body1">
          First charge <strong>{fmtDate(billing.trialEndDate)}</strong> (when
          your trial ends)
        </Typography>
      ) : (
        <Typography variant="body1">
          Free trial ends <strong>{fmtDate(billing.trialEndDate)}</strong> — add
          a payment method to keep your plan.
        </Typography>
      );
    } else if (status === "active" && billing.currentPeriodEnd) {
      chargeDateLine = (
        <Typography variant="body1">
          Next charge <strong>{fmtDate(billing.currentPeriodEnd)}</strong>
        </Typography>
      );
    }
  }

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        Billing
      </Typography>

      {/* Deliberately not gating the buttons on a saved address: checkout falls
          back to the address already on the customer, so an existing subscriber
          changing plans must not be blocked into re-entering one. Someone with
          no stored address gets checkout's explicit error, which this form
          answers directly. */}
      <BillingAddressForm
        value={billingAddress}
        onChange={setBillingAddress}
        onSaved={setSavedAddress}
        // `busy`, not a subset of it: the form must also lock while the Helcim
        // iframe is open. prepareCheckout has already sent the address by then,
        // so a save made behind the iframe would leave the Helcim customer
        // holding an address the form no longer shows.
        disabled={busy}
      />

      {isWelcome && !hasPlanTied && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setSearchParams({}, { replace: true })}
        >
          Welcome to PDFGlyph! Pick a plan below to get started. Every paid plan
          includes a 7-day free trial — no credit card required.
        </Alert>
      )}

      {isSubscribed && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSearchParams({}, { replace: true })}
        >
          Your subscription is active. Welcome aboard!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {billing?.subscriptionStatus === "past_due" &&
        (() => {
          // PDF-13: prefer the server-computed cancellation deadline. Fall
          // back to a generic message if the field is missing (legacy users
          // whose pastDueSince was never stamped by the webhook).
          const graceEnd = billing.gracePeriodEndDate
            ? new Date(billing.gracePeriodEndDate)
            : null;
          const daysLeft = graceEnd
            ? Math.max(
                0,
                Math.ceil(
                  (graceEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
                ),
              )
            : null;
          const graceEndLabel = graceEnd
            ? graceEnd.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : null;
          return (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Your last payment failed.
                </Typography>
                <Typography variant="body2">
                  {graceEndLabel
                    ? `Your account will be canceled on ${graceEndLabel}${
                        daysLeft != null
                          ? ` (${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining)`
                          : ""
                      } unless payment succeeds.`
                    : "Your account will be canceled soon unless payment succeeds."}
                </Typography>
                <Box sx={{ pt: 0.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    color="inherit"
                    onClick={openHelcimCardUpdate}
                    disabled={busy}
                    startIcon={
                      checkoutTarget === "card" ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CreditCardIcon />
                      )
                    }
                    sx={{
                      bgcolor: "background.paper",
                      color: "error.dark",
                      "&:hover": { bgcolor: "background.default" },
                    }}
                  >
                    {checkoutTarget === "card" ? "Loading..." : "Retry Payment"}
                  </Button>
                </Box>
              </Stack>
            </Alert>
          );
        })()}

      {isTrialing && !trialExpired && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have{" "}
          <strong>
            {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""}
          </strong>{" "}
          left in your free trial.
          {!billing?.hasPaymentMethod &&
            " Add a payment method to avoid service interruption."}
          {billing?.hasPaymentMethod &&
            " Your card will not be charged until the trial ends."}
        </Alert>
      )}

      {(trialExpired || isExpiredTrial) && !billing?.hasPaymentMethod && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your free trial has ended. Add a payment method to continue using your
          plan.
        </Alert>
      )}

      {/* Current Plan Summary */}
      {billing && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Typography variant="body2" color="text.secondary">
                Current Plan
              </Typography>
              {billing.effectiveCancelAtPeriodEnd ? (
                <Chip color="warning" size="small" label="Canceled" />
              ) : billing.subscriptionStatus ? (
                <Chip
                  label={
                    billing.subscriptionStatus === "trialing"
                      ? "Free Trial"
                      : billing.subscriptionStatus === "expired_trial"
                        ? "Trial Expired"
                        : billing.subscriptionStatus
                  }
                  size="small"
                  color={
                    billing.subscriptionStatus === "active"
                      ? "success"
                      : billing.subscriptionStatus === "trialing"
                        ? "info"
                        : "warning"
                  }
                />
              ) : null}
            </Stack>
            <Typography variant="h6" component="h2" fontWeight={700}>
              {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {billing.rendersUsed.toLocaleString()} /{" "}
              {billing.rendersLimit.toLocaleString()} renders used this month
            </Typography>
            {chargeDateLine}
            <Box sx={{ mt: 1 }}>
              <Link
                component={RouterLink}
                to="/dashboard/billing/history"
                variant="body2"
                underline="hover"
              >
                View payment history →
              </Link>
            </Box>
            {billing.effectiveCancelAtPeriodEnd && billing.currentPeriodEnd && (
              <Typography variant="body2" color="text.secondary">
                Access until{" "}
                {dayjs.utc(billing.currentPeriodEnd).format("MMM D, YYYY")}
              </Typography>
            )}
            {billing.effectiveCancelAtPeriodEnd && billing.canceledAt && (
              <Typography variant="body2" color="text.secondary">
                Canceled {dayjs.utc(billing.canceledAt).format("MMM D, YYYY")}
              </Typography>
            )}
            {billing.effectiveCancelAtPeriodEnd && (
              <Button
                variant="contained"
                disabled={busy}
                sx={{ mt: 1 }}
                onClick={handleResubscribe}
              >
                {resubscribing ? "Resubscribing..." : "Resubscribe"}
              </Button>
            )}
            {billing.plan !== "free" &&
              !billing.effectiveCancelAtPeriodEnd &&
              billing.subscriptionStatus !== "expired_trial" && (
                <Button
                  variant="text"
                  size="small"
                  color="inherit"
                  disabled={busy}
                  sx={{ mt: 1, p: 0, minWidth: 0, textDecoration: "underline" }}
                  onClick={() => {
                    setCancelError(null);
                    setCancelDialogOpen(true);
                  }}
                >
                  Cancel subscription
                </Button>
              )}
          </CardContent>
        </Card>
      )}

      {/* Payment Method Section — only shown for users with a subscription */}
      {billing?.subscriptionStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {billing.effectiveCancelAtPeriodEnd ? (
              <Typography variant="body2" color="text.secondary">
                Payment method removed when you canceled. Adding a card happens
                at resubscribe.
              </Typography>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <CreditCardIcon color="action" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Payment Method
                  </Typography>
                </Stack>
                {billing?.hasPaymentMethod ? (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      {billing.cardBrand && billing.cardLast4
                        ? `${normalizeCardBrand(billing.cardBrand)} ending in ${billing.cardLast4}`
                        : "A payment method is on file."}
                      {isTrialing && (
                        <>
                          <br />
                          Your card will not be charged until your free trial
                          ends.
                        </>
                      )}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={busy}
                      onClick={openHelcimCardUpdate}
                      startIcon={
                        checkoutTarget === "card" ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                    >
                      {checkoutTarget === "card"
                        ? "Loading..."
                        : "Update Payment Method"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      No payment method on file.
                      {isTrialing &&
                        ` Add one now to avoid service interruption when your trial ends${trialDaysLeft > 0 ? ` in ${trialDaysLeft} days` : ""}. Your card will not be charged until the trial is over.`}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={busy}
                      startIcon={
                        checkoutTarget === "card" ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CreditCardIcon />
                        )
                      }
                      onClick={openHelcimCardUpdate}
                    >
                      {checkoutTarget === "card"
                        ? "Loading..."
                        : "Add Payment Method"}
                    </Button>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Divider sx={{ mb: 3 }} />

      {!billing?.effectiveCancelAtPeriodEnd && (
        <>
          {/* Billing Cycle Toggle */}
          <Box display="flex" justifyContent="center" mb={3}>
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              onChange={(_, value) => value && setBillingCycle(value)}
              size="small"
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="annual">
                Annual
                <Chip
                  label="Save ~17%"
                  size="small"
                  color="success"
                  sx={{ ml: 1 }}
                />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Plan Cards */}
          <Grid container spacing={3}>
            {PLANS_CATALOG.map((plan) => {
              const isCurrentPlan = plan.slug === currentPlan;
              const cta = getTileCta(
                plan.slug,
                currentPlan,
                billing?.subscriptionStatus ?? null,
              );
              const hasAnnualPrice = plan.annualPriceCents != null;
              const price =
                billingCycle === "annual" && hasAnnualPrice
                  ? plan.annualPriceCents!
                  : plan.monthlyPriceCents;
              let priceLabel: string | null = null;
              let periodLabel = "";
              if (plan.monthlyPriceCents !== null && price !== null) {
                if (price === 0) {
                  priceLabel = "Free";
                  periodLabel = "";
                } else {
                  priceLabel = `$${(price / 100).toFixed(0)}`;
                  periodLabel =
                    billingCycle === "annual" && hasAnnualPrice
                      ? "/year"
                      : "/month";
                }
              }

              return (
                <Grid item xs={12} sm={6} md={4} key={plan.slug}>
                  <Card
                    data-testid={`plan-tile-${plan.slug}`}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      border: isCurrentPlan ? 2 : 1,
                      borderColor: isCurrentPlan ? "primary.main" : "divider",
                    }}
                  >
                    <CardContent sx={{ flex: 1 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="h6" component="h2">
                          {plan.name}
                        </Typography>
                        {isCurrentPlan && (
                          <Chip label="Current" size="small" color="primary" />
                        )}
                      </Stack>
                      {plan.monthlyPriceCents !== null &&
                        priceLabel !== null && (
                          <Typography
                            variant="h4"
                            component="h3"
                            fontWeight={700}
                            data-testid={`plan-price-${plan.slug}`}
                            sx={{ my: 1 }}
                          >
                            {priceLabel}
                            <Typography
                              variant="body2"
                              component="span"
                              color="text.secondary"
                            >
                              {periodLabel}
                            </Typography>
                          </Typography>
                        )}
                      {/* CHK-39: feature bullets come from the local catalog
                          only — the checkout plans API no longer returns
                          `features`. planFeatures() is non-throwing and
                          yields [] for slugs the local catalog lacks. */}
                      <Stack spacing={0.5}>
                        {planFeatures(plan.slug).map((feature) => (
                          <Typography key={feature} variant="body2">
                            {"\u2713"} {feature}
                          </Typography>
                        ))}
                      </Stack>
                    </CardContent>
                    <Box p={2}>
                      {cta.kind === "current" ? (
                        <Button fullWidth variant="outlined" disabled>
                          Your plan
                        </Button>
                      ) : cta.kind === "cancel" ? (
                        // Downgrade-to-Free reuses the existing cancel-at-period-end
                        // flow (the only way to land on Free is to cancel the paid
                        // plan; subscription-sweep flips plan to "free" at period end).
                        // Label "Downgrade" to match the other tile actions; behavior
                        // remains identical to the inline "Cancel subscription" link.
                        <Button
                          fullWidth
                          variant="outlined"
                          disabled={busy}
                          startIcon={<ArrowDownwardIcon />}
                          onClick={() => {
                            setCancelError(null);
                            setCancelDialogOpen(true);
                          }}
                        >
                          Downgrade
                        </Button>
                      ) : cta.kind === "contact-sales" ? (
                        <Button
                          fullWidth
                          variant="outlined"
                          href="mailto:sales@pdfglyph.com"
                        >
                          Contact Sales
                        </Button>
                      ) : cta.kind === "upgrade" ? (
                        <Button
                          fullWidth
                          variant="contained"
                          disabled={busy}
                          startIcon={
                            changingPlan === plan.slug ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <ArrowUpwardIcon />
                            )
                          }
                          onClick={() =>
                            setPendingUpgrade({
                              planSlug: plan.slug,
                              planName: plan.name,
                            })
                          }
                        >
                          {changingPlan === plan.slug
                            ? "Upgrading..."
                            : "Upgrade"}
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          variant="outlined"
                          disabled={busy}
                          startIcon={
                            changingPlan === plan.slug ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <ArrowDownwardIcon />
                            )
                          }
                          onClick={() =>
                            setPendingDowngrade({
                              planSlug: plan.slug,
                              planName: plan.name,
                            })
                          }
                        >
                          {changingPlan === plan.slug
                            ? "Downgrading..."
                            : "Downgrade"}
                        </Button>
                      )}
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Upgrade confirmation dialog */}
      <Dialog
        open={pendingUpgrade !== null}
        onClose={() => setPendingUpgrade(null)}
      >
        <DialogTitle>Confirm Upgrade</DialogTitle>
        <DialogContent>
          {(() => {
            if (!pendingUpgrade || !billing) return null;
            const isActive = billing.subscriptionStatus === "active";
            const newPlan = plans.find(
              (p) => p.slug === pendingUpgrade.planSlug,
            );
            const oldPlan = plans.find((p) => p.slug === currentPlan);
            const newPrice = newPlan
              ? billingCycle === "annual" && newPlan.annualPriceCents != null
                ? newPlan.annualPriceCents
                : newPlan.monthlyPriceCents
              : 0;
            const oldPrice = oldPlan
              ? billingCycle === "annual" && oldPlan.annualPriceCents != null
                ? oldPlan.annualPriceCents
                : oldPlan.monthlyPriceCents
              : 0;
            const periodDays = billingCycle === "annual" ? 365 : 30;
            const daysLeft = billing.currentPeriodEnd
              ? Math.min(periodDays, daysUntil(billing.currentPeriodEnd))
              : 0;
            const proRatedCents = isActive
              ? Math.round(((newPrice - oldPrice) * daysLeft) / periodDays)
              : 0;
            const overageRenders = Math.max(
              0,
              billing.rendersUsed - billing.rendersLimit,
            );
            const overageCents = overageRenders * OVERAGE_RATE_CENTS_PER_RENDER;
            const overageRateLabel = `$${(OVERAGE_RATE_CENTS_PER_RENDER / 100).toFixed(2)}`;

            return (
              <>
                <DialogContentText sx={{ mb: 2 }}>
                  Your plan will change from{" "}
                  <strong>
                    {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
                  </strong>{" "}
                  to <strong>{pendingUpgrade.planName}</strong> immediately.
                  {isTrialing &&
                    " Your trial will continue with the new plan\u2019s features."}
                </DialogContentText>
                {isActive && (
                  <Box
                    sx={{
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      p: 2,
                      mb: 1,
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">
                          Pro-rated charge ({daysLeft} day
                          {daysLeft !== 1 ? "s" : ""} remaining)
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          ${(proRatedCents / 100).toFixed(2)}
                        </Typography>
                      </Stack>
                      {overageRenders > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2">
                            Overage ({overageRenders.toLocaleString()} render
                            {overageRenders !== 1 ? "s" : ""} at{" "}
                            {overageRateLabel}/render)
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            ${(overageCents / 100).toFixed(2)}
                          </Typography>
                        </Stack>
                      )}
                      <Divider sx={{ my: 0.5 }} />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={700}>
                          Total immediate charge
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          ${((proRatedCents + overageCents) / 100).toFixed(2)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                )}
                {isActive && overageRenders > 0 && (
                  <DialogContentText variant="caption" sx={{ mt: 1 }}>
                    Future overage will be calculated against your new{" "}
                    {pendingUpgrade.planName} plan limit.
                  </DialogContentText>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingUpgrade(null)}>Cancel</Button>
          <Button
            onClick={handleUpgradeConfirm}
            color="primary"
            variant="contained"
          >
            Confirm Upgrade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downgrade confirmation dialog */}
      <Dialog
        open={pendingDowngrade !== null}
        onClose={() => setPendingDowngrade(null)}
      >
        <DialogTitle>Confirm Downgrade</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your plan will change from{" "}
            <strong>
              {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
            </strong>{" "}
            to <strong>{pendingDowngrade?.planName}</strong> at the end of your
            current billing period. You&apos;ll continue to have access to your
            current plan&apos;s features until then.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDowngrade(null)}>Cancel</Button>
          <Button
            onClick={handleDowngradeConfirm}
            color="warning"
            variant="contained"
          >
            Confirm Downgrade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel subscription dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelLoading && closeCancelDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Your subscription will cancel at the end of the current billing
            period. You&apos;ll keep access to all features until{" "}
            <strong>
              {billing?.currentPeriodEnd
                ? dayjs.utc(billing.currentPeriodEnd).format("MMM D, YYYY")
                : "the end of your billing period"}
            </strong>
            .
          </DialogContentText>

          {cancelError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cancelError}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Why are you canceling? (optional)</InputLabel>
            <Select
              value={cancelReason}
              label="Why are you canceling? (optional)"
              onChange={(e) =>
                setCancelReason(e.target.value as CancelReason | "")
              }
            >
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              <MenuItem value="too_expensive">Too expensive</MenuItem>
              <MenuItem value="not_using">Not using it enough</MenuItem>
              <MenuItem value="missing_feature">
                Missing a feature I need
              </MenuItem>
              <MenuItem value="switching_tools">
                Switching to another tool
              </MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional feedback (optional)"
            value={cancelDetail}
            onChange={(e) => setCancelDetail(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={closeCancelDialog}
            disabled={cancelLoading}
          >
            Keep subscription
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelSubscription}
            disabled={cancelLoading}
          >
            {cancelLoading ? "Canceling..." : "Cancel subscription"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
